/**
 * PasskeyVerifyFlow — passkey (WebAuthn) login challenge (WA-1b).
 *
 * Consumed by ``AdminMfaVerify`` when the pending login has
 * ``available_methods`` containing ``"webauthn"``. Flow:
 *
 *   POST verify/begin → startAuthentication({ optionsJSON }) →
 *   POST verify/finish → loginSuccess + onComplete.
 *
 * Error handling per brief:
 *   - ``NotAllowedError`` from @simplewebauthn → inline "Passkey prompt was
 *     cancelled — try again." MUST NOT call finish (no lockout charge).
 *   - verify/finish 401 → "That didn't work, try again." re-begin available.
 *   - verify/begin 429 ``mfa_too_many_attempts`` → amber lockout banner.
 *   - ``!window.PublicKeyCredential`` → graceful "browser doesn't support
 *     passkeys" copy + route to TOTP fallback via ``onFallback``.
 *   - 401 ``invalid_token`` (mfa_pending expired) → expired card + onExpired.
 */
import { useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { startAuthentication } from "@simplewebauthn/browser";
import { Button } from "@/components/common/Button";
import { useAuthStore } from "@/stores/useAuthStore";
import { ApiError } from "@/lib/apiError";
import type {
  WebauthnBeginResponse,
  WebauthnFinishResponse,
} from "@/hooks/queries/admin/useAdminWebauthn";

type BeginMutation = UseMutationResult<WebauthnBeginResponse, ApiError, void>;
type FinishMutation = UseMutationResult<
  WebauthnFinishResponse,
  ApiError,
  { credential: Record<string, unknown> }
>;

interface PasskeyVerifyFlowProps {
  beginMutation: BeginMutation;
  finishMutation: FinishMutation;
  /** Called after successful verification + session wired. */
  onComplete: () => void;
  /** Called when mfa_pending token has expired. */
  onExpired: () => void;
  /**
   * Rendered below the primary action — lets the caller expose a TOTP
   * fallback link / toggle. Required so the UI never leaves the user
   * without an escape route.
   */
  fallbackSlot?: React.ReactNode;
}

type Step = "idle" | "ceremony" | "finishing";

type Banner =
  | { kind: "none" }
  | { kind: "cancelled" }
  | { kind: "error"; text: string }
  | { kind: "lockout"; minutes: number };

/** Returns true if the browser supports WebAuthn. */
function webAuthnSupported(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

export function PasskeyVerifyFlow({
  beginMutation,
  finishMutation,
  onComplete,
  onExpired,
  fallbackSlot,
}: PasskeyVerifyFlowProps): JSX.Element {
  const [step, setStep] = useState<Step>("idle");
  const [banner, setBanner] = useState<Banner>({ kind: "none" });
  const [expired, setExpired] = useState(false);

  // ── Expired state ────────────────────────────────────────────────────
  if (expired) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          Your sign-in attempt timed out. Sign in again.
        </p>
        <Button type="button" className="w-full" onClick={onExpired}>
          Back to sign in
        </Button>
      </div>
    );
  }

  // ── Browser check ────────────────────────────────────────────────────
  // Render even in unsupported-browser case so fallbackSlot is reachable.
  if (!webAuthnSupported()) {
    return (
      <div className="flex flex-col gap-4">
        <div
          role="alert"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          This browser doesn&rsquo;t support passkeys &mdash; use your
          authenticator code instead.
        </div>
        {fallbackSlot}
      </div>
    );
  }

  const isBusy = step === "ceremony" || step === "finishing";
  const isLockedOut = banner.kind === "lockout";

  const handleVerify = (): void => {
    if (isBusy || isLockedOut) return;
    setBanner({ kind: "none" });
    setStep("ceremony");

    beginMutation.mutate(undefined, {
      onSuccess: async (data) => {
        let assertResp: Awaited<ReturnType<typeof startAuthentication>>;
        try {
          assertResp = await startAuthentication({
            optionsJSON: data.options as unknown as Parameters<typeof startAuthentication>[0]["optionsJSON"],
          });
        } catch (err) {
          // User cancelled — do NOT call finish (no lockout charge).
          const isUserCancel =
            err instanceof Error && err.name === "NotAllowedError";
          setStep("idle");
          if (isUserCancel) {
            setBanner({ kind: "cancelled" });
          } else {
            const msg =
              err instanceof Error ? err.message : "Passkey error. Try again.";
            setBanner({ kind: "error", text: msg });
          }
          return;
        }

        setStep("finishing");
        finishMutation.mutate(
          { credential: assertResp as unknown as Record<string, unknown> },
          {
            onSuccess: (resp) => {
              useAuthStore.getState().loginSuccess(resp.access_token);
              onComplete();
            },
            onError: (err) => {
              setStep("idle");
              if (err.status === 401 && err.error === "invalid_token") {
                setExpired(true);
              } else if (err.status === 401) {
                // Generic — don't reveal which credential failed.
                setBanner({
                  kind: "error",
                  text: "That didn't work, try again.",
                });
              } else if (err.status === 429) {
                const detail = err.details as { retry_after_seconds?: number };
                const minutes = detail.retry_after_seconds
                  ? Math.ceil(detail.retry_after_seconds / 60)
                  : 15;
                setBanner({ kind: "lockout", minutes });
              } else {
                setBanner({
                  kind: "error",
                  text: "Couldn't start passkey challenge. Try again in a moment.",
                });
              }
            },
          },
        );
      },
      onError: (err) => {
        setStep("idle");
        if (err.status === 401 && err.error === "invalid_token") {
          setExpired(true);
        } else if (err.status === 429) {
          const detail = err.details as { retry_after_seconds?: number };
          const minutes = detail.retry_after_seconds
            ? Math.ceil(detail.retry_after_seconds / 60)
            : 15;
          setBanner({ kind: "lockout", minutes });
        } else {
          setBanner({
            kind: "error",
            text: "Couldn't start passkey challenge. Try again in a moment.",
          });
        }
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Banners */}
      {banner.kind === "cancelled" ? (
        <div
          role="alert"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          Passkey prompt was cancelled &mdash; try again.
        </div>
      ) : null}
      {banner.kind === "lockout" ? (
        <div
          role="alert"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          Too many attempts. Try again in {banner.minutes}{" "}
          {banner.minutes === 1 ? "minute" : "minutes"}.
        </div>
      ) : null}
      {banner.kind === "error" ? (
        <div
          role="alert"
          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400"
        >
          {banner.text}
        </div>
      ) : null}

      {step === "ceremony" ? (
        <p
          aria-live="polite"
          className="text-sm text-text-secondary dark:text-brand-100/70"
        >
          Waiting for your device&hellip;
        </p>
      ) : null}

      <Button
        type="button"
        className="w-full"
        loading={isBusy}
        disabled={isBusy || isLockedOut}
        onClick={handleVerify}
      >
        Use passkey
      </Button>

      {/* Fallback slot: TOTP toggle link or secondary method button */}
      {fallbackSlot}
    </div>
  );
}
